// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useState } from 'react'
import clsx from 'clsx'
import styles from './LabCompareBody.module.css'
import LabChartPanel, {
  labTabsFor,
  shownTab,
  measuresDrawn,
  toggleKey,
  PRIMARY_KEY,
  TARGET_KEY,
  type LabTab,
  type PanelOverlay,
} from 'components/product/LabChartPanel'
import {
  GEOM,
  GEOM_COMPACT,
  tabMeasure,
  type ChartGeometry,
  type LabChartPaths,
  type LabTabKey,
} from 'lib/dealhunt/audioChart'
import type { MdaqsScore } from 'lib/dealhunt/review'
import type { DealHuntMdaqs } from 'lib/dealhunt/types'
import { GUIDE_COPY } from 'lib/owner-notes/band-copy'
import { useDeclaredContext } from 'lib/owner-notes/declared'
import { useCurvesFor, YOURS_COLOR } from 'lib/owner-notes/curves'
import {
  MAX_PINS,
  PIN_COLORS,
  compareTokensFor,
  useLabCompare,
  type LabTarget,
} from 'lib/owner-notes/lab-compare'
import { compareHref } from 'lib/dealhunt/url'
import { publicConfig } from 'config'

export interface LabCompareBodyProps {
  /** The chart's DOM id prefix — one per mounted chart on the page. */
  idPrefix: string
  /** The chart frame, chosen by the host from ITS content width (never the breakpoint, never a
   *  ResizeObserver): `wide` = the product page's 600 × 250, `compact` = 600 × 380. */
  frame: 'compact' | 'wide'
  /** What the hint asks the reader to do on the chart: "Hover" with a pointer, "Tap" on a phone. */
  readVerb: 'Hover' | 'Tap'
  className?: string
  /** Optional controlled tab, for a host that outlives this body (the desktop panel keeps the
   *  tab across segment switches and collapse). Uncontrolled when absent. */
  active?: LabTabKey
  onTabChange?: (key: LabTabKey) => void
  /** "Open the full comparison" under the chart; off where the host has its own (the review's
   *  versus panel foot). */
  fullLink?: boolean
  /** False: the pins alone, as on the compare page, with no declared pair drawn or linked (the
   *  review's versus panel, which offers no way to change it). */
  yours?: boolean
  /** Each pin's audio quality (MDAQS), from a host that has loaded the products (the review's
   *  versus panel): undefined while it loads, null when it has none. Absent: no Audio quality
   *  tab. */
  mdaqsOf?: (nodeId: string) => MdaqsScore[] | null | undefined
}

/** A review's MDAQS scores as an overlay's bars; null when it has none. */
function mdaqsBars(scores: MdaqsScore[] | null | undefined): DealHuntMdaqs | null {
  if (!scores?.length) {
    return null
  }
  const of = (k: string): number | null => scores.find(m => m.key === `mdaqs_${k}`)?.score ?? null
  return {
    timbre: of('timbre'),
    distortion: of('distortion'),
    immersiveness: of('immersiveness'),
    overall: of('overall'),
  }
}

/** Test hook for the empty state (phases 2–3 assert on it). */
export const COMPARE_EMPTY_TEST_ID = 'lab-compare-empty'

/**
 * What "Compare" shows (extracted 2026-10-19 from `LabCompareDrawer`): the pins overlaid on the
 * product page's chart panel, the pinned list as the chart key (Unpin lives on its rows), and
 * the hint. Shared by the desktop panel and the phone pick sheet — each host owns its chrome
 * (head, Clear, segments, size) and hands down only the frame and the read verb; everything
 * shown is read here from the page's pins and declaration.
 *
 * No declaration is a first-class state (2026-09-15): the first pin is then the primary curve,
 * the rest overlay it, and nothing says "yours". Nothing pinned: the empty state only — no
 * chart, no key, no hint.
 */
const LabCompareBody: React.VFC<LabCompareBodyProps> = ({
  idPrefix,
  frame,
  readVerb,
  className,
  active: activeProp,
  onTabChange,
  fullLink = true,
  yours = true,
  mdaqsOf,
}) => {
  const lab = useLabCompare()
  const declaredCtx = useDeclaredContext()?.declared ?? null
  const declared = yours ? declaredCtx : null
  const pinned: LabTarget[] = lab?.pinned ?? []
  const maxPins = lab?.maxPins ?? MAX_PINS
  const { curves } = useCurvesFor([
    declared?.clearbuyProductId ?? null,
    ...pinned.map(p => p.clearbuyProductId),
  ])
  const [ownActive, setOwnActive] = useState<LabTabKey>('freq')
  // Highlighted curves: the pinned list is the chart's key, so its rows toggle them too.
  const [focus, setFocus] = useState<ReadonlySet<string>>(() => new Set())

  if (!lab) {
    return null
  }

  if (pinned.length === 0) {
    return (
      <div
        className={clsx(styles.root, styles.empty, className)}
        data-frame={frame}
        data-testid={COMPARE_EMPTY_TEST_ID}
        role="status"
      >
        <span className={styles.emptyBox} aria-hidden="true" />
        <p className={styles.emptyTitle}>{GUIDE_COPY.compare.emptyTitle}</p>
        <p className={styles.emptyBody}>{GUIDE_COPY.compare.emptyBody(maxPins - 1)}</p>
      </div>
    )
  }

  const geom: ChartGeometry = frame === 'wide' ? GEOM : GEOM_COMPACT
  const myId = declared?.clearbuyProductId ?? null
  const myCurves = myId === null ? null : curves[myId]
  // The declared pair is the primary curve when the lab measured it; otherwise the first
  // pinned card is, and the pair is listed as "no data".
  const primaryIsYours = Boolean(myCurves && declared)
  const primary =
    primaryIsYours && declared
      ? { name: `${declared.name} · yours`, color: YOURS_COLOR, curves: myCurves as LabChartPaths }
      : null
  const overlayTargets = primaryIsYours ? pinned : pinned.slice(1)
  const fallbackPrimary = !primaryIsYours && pinned[0] ? pinned[0] : null
  const fallbackCurves =
    fallbackPrimary?.clearbuyProductId !== null && fallbackPrimary?.clearbuyProductId !== undefined
      ? curves[fallbackPrimary.clearbuyProductId]
      : null

  const overlays: PanelOverlay[] = overlayTargets.map((p, i) => ({
    key: p.nodeId,
    name: p.name,
    color: PIN_COLORS[(primaryIsYours ? i : i + 1) % PIN_COLORS.length],
    curves: p.clearbuyProductId === null ? null : curves[p.clearbuyProductId],
    mdaqs: mdaqsBars(mdaqsOf?.(p.nodeId)),
  }))
  if (!primaryIsYours && declared) {
    overlays.unshift({
      key: 'yours',
      name: `${declared.name} · yours`,
      color: YOURS_COLOR,
      curves: myId === null ? null : myCurves,
      mdaqs: null,
    })
  }

  const compareTokens = compareTokensFor(declared, pinned)

  // The declared pair is no pin: the host has no MDAQS for it.
  const primaryMdaqs = fallbackPrimary && mdaqsOf ? mdaqsOf(fallbackPrimary.nodeId) : null
  const hasMdaqs = Boolean(primaryMdaqs?.length) || primaryMdaqs === undefined
  const panelCurves = primary?.curves ?? fallbackCurves ?? null
  // `curves` holds no entry for a product until its fetch settles (null = the lab has none).
  const chartLoading = !primary && fallbackPrimary !== null && fallbackCurves === undefined
  const panelName = primary?.name ?? fallbackPrimary?.name ?? ''
  const panelColor = primary ? YOURS_COLOR : PIN_COLORS[0]
  const onTabClick = (t: LabTab): void => {
    setOwnActive(t.key)
    onTabChange?.(t.key)
  }
  // The tabs are every measure any curve on the chart carries (`tabsFrom="any"`, 2026-10-18),
  // so unpinning can take the active tab's measure away; land on the first tab that is left.
  const tabsLeft = panelCurves
    ? labTabsFor(measuresDrawn(panelCurves, overlays, 'any'), hasMdaqs)
    : []
  const active = shownTab(tabsLeft, activeProp ?? ownActive)

  // The pinned list IS the chart key (the panel's own legend is off): one row per curve in its
  // overlay colour, the declared pair first, "no data" muted where the lab has nothing, the
  // preference curve as a dashed row on the frequency tab. Unpin lives here, next to the name.
  const measure = active === 'mdaqs' || !panelCurves ? null : tabMeasure(active, panelCurves)
  const status = (
    c: LabChartPaths | null | undefined,
    nodeId: string | null = null
  ): 'ok' | 'loading' | 'none' => {
    if (active === 'mdaqs') {
      const scores = nodeId && mdaqsOf ? mdaqsOf(nodeId) : null
      if (scores === undefined) {
        return 'loading'
      }
      return scores?.length ? 'ok' : 'none'
    }
    if (c === undefined) {
      return 'loading'
    }
    // The attenuation chart draws whichever of isolation / ANC a product has.
    const has =
      active === 'atten' ? Boolean(c?.isolation || c?.anc) : Boolean(measure && c?.[measure])
    return has ? 'ok' : 'none'
  }
  const rows: Array<{
    /** The curve's key on the chart (`PRIMARY_KEY` for the primary). */
    key: string
    nodeId: string | null
    name: string
    color: string
    status: 'ok' | 'loading' | 'none'
    removable: boolean
  }> = [
    ...(declared
      ? [
          {
            key: primaryIsYours ? PRIMARY_KEY : 'yours',
            nodeId: null,
            name: `${declared.name} · yours`,
            color: YOURS_COLOR,
            status: myId === null ? ('none' as const) : status(myCurves),
            removable: false,
          },
        ]
      : []),
    ...pinned.map((p, i) => ({
      key: !primaryIsYours && i === 0 ? PRIMARY_KEY : p.nodeId,
      nodeId: p.nodeId,
      name: p.name,
      color: PIN_COLORS[i % PIN_COLORS.length],
      status:
        p.clearbuyProductId === null && active !== 'mdaqs'
          ? ('none' as const)
          : status(p.clearbuyProductId === null ? null : curves[p.clearbuyProductId], p.nodeId),
      removable: true,
    })),
  ]
  const showTarget = measure === 'frequency'
  const anyFocus =
    rows.some(r => r.status === 'ok' && focus.has(r.key)) || (showTarget && focus.has(TARGET_KEY))
  const toggle = (key: string, swatch: JSX.Element, name: string): JSX.Element => (
    <button
      type="button"
      className={styles.pinToggle}
      aria-pressed={focus.has(key)}
      data-dimmed={(anyFocus && !focus.has(key)) || undefined}
      onClick={() => setFocus(toggleKey(focus, key))}
    >
      {swatch}
      <span className={styles.pinName} title={name}>
        {name}
      </span>
    </button>
  )

  return (
    <div className={clsx(styles.root, className)} data-frame={frame}>
      {chartLoading && (
        <div className={styles.chartSkeleton} role="status" aria-label="Loading lab curves">
          <span className={styles.bone} style={{ width: '60%', height: '2rem' }} />
          <span className={styles.bone} style={{ height: '13rem' }} />
        </div>
      )}
      {!panelCurves && !chartLoading && (
        <p className={styles.loading}>{'No lab measurements to draw yet.'}</p>
      )}
      {panelCurves ? (
        <LabChartPanel
          className={styles.panel}
          idPrefix={idPrefix}
          productName={panelName}
          primaryColor={panelColor}
          curves={panelCurves}
          mdaqs={primaryMdaqs ?? []}
          mdaqsPending={primaryMdaqs === undefined}
          overlays={overlays}
          active={active}
          onTabClick={onTabClick}
          geom={geom}
          tables={false}
          legend={false}
          blurbs={false}
          tabsFrom="any"
          focus={focus}
          onFocusChange={setFocus}
        />
      ) : null}
      <ul className={styles.pins} aria-label="Curves on the chart">
        {rows.map(r => (
          <li
            key={r.nodeId ?? 'yours'}
            className={r.status === 'ok' ? styles.pin : `${styles.pin} ${styles.pinMuted}`}
          >
            {r.status === 'ok' ? (
              toggle(
                r.key,
                <span className={styles.swatch} style={{ backgroundColor: r.color }} />,
                r.name
              )
            ) : (
              <>
                <span className={styles.swatchHollow} />
                <span className={styles.pinName} title={r.name}>
                  {r.name}
                </span>
              </>
            )}
            {r.status !== 'ok' && (
              <span className={styles.pinStatus}>{r.status === 'loading' ? '…' : 'no data'}</span>
            )}
            {r.removable && r.nodeId && (
              <button
                type="button"
                className={styles.unpin}
                aria-label={`Unpin ${r.name}`}
                title="Unpin"
                onClick={() => lab.unpin(r.nodeId as string)}
              >
                {'×'}
              </button>
            )}
          </li>
        ))}
        {showTarget && (
          <li className={`${styles.pin} ${styles.pinMuted}`}>
            {toggle(
              TARGET_KEY,
              <span className={styles.swatchDash} />,
              'SoundGuys preference curve'
            )}
          </li>
        )}
      </ul>
      <p className={styles.hint}>
        {/* "Tap" on a phone, "Hover" on desktop: the readout answers both, the word says which
        (2026-10-17: the sheet said "Hover" and "the grid" on a guide's phone). */}
        {pinned.length < maxPins
          ? `Pin ${
              maxPins - pinned.length
            } more from the list. ${readVerb} the chart to read every curve at one frequency.`
          : `${readVerb} the chart to read every curve at one frequency. Unpin one to make room.`}
      </p>
      {fullLink && (
        <a
          className={styles.full}
          href={compareHref(compareTokens, Boolean(publicConfig('useTrailingSlash')))}
        >
          {'Open the full comparison →'}
        </a>
      )}
    </div>
  )
}

export default LabCompareBody
